// src/pages/SchedulerAdmin.tsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";

/* === Firestore (Scheduler EU-ready) === */
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { fs } from "../firebase";

/* === Realtime DB (for legacy name lookups only; left intact) === */
import { getDatabase, ref as rref, get as rget } from "firebase/database";
const db = getDatabase();

/* ---------------- Types ---------------- */

type Pod = {
  name: string;
  active?: boolean;
};

type Booking = {
  podId: string;
  startMs: number;
  endMs: number;
  userId: string;
  userName?: string; // NEW: stored at booking time
  byRole?: "faculty" | "admin";
};

type Pending = {
  podId: string;
  dayLocal: Date;
  hh: number;
  mm: number;
  x: number;
  y: number;
};

// Helper: resolve a friendly display name at booking time without any UI changes
function getDisplayUserName(): string {
  const stored = localStorage.getItem("profileName");
  if (stored && stored.trim().length > 0) return stored.trim();
  const u = auth.currentUser;
  if (u?.displayName && u.displayName.trim().length > 0)
    return u.displayName.trim();
  if (u?.email) return u.email.split("@")[0];
  return "Faculty";
}

/* --------------- Utilities --------------- */

function startOfIsoWeekLocal(d: Date) {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const day = copy.getDay(); // Sun=0..Sat=6
  const mondayDiff = (day + 6) % 7;
  copy.setDate(copy.getDate() - mondayDiff);
  copy.setHours(0, 0, 0, 0);
  return copy;
}
function addDays(d: Date, days: number) {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + days);
  return startOfIsoWeekLocal(x);
}
function getWeekDaysFrom(weekStartLocal: Date) {
  return Array.from(
    { length: 7 },
    (_, i) => new Date(weekStartLocal.getTime() + i * 86400000)
  );
}
function buildTimeGrid(fromHour: number, toHour: number) {
  const out: { hh: number; mm: number }[] = [];
  for (let h = fromHour; h <= toHour; h++) {
    for (let m = 0; m < 60; m += 15) out.push({ hh: h, mm: m });
  }
  return out;
}
function pad(n: number) {
  return String(n).padStart(2, "0");
}
function fmtDate(d: Date) {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* --------------- Component --------------- */

export default function SchedulerAdmin() {
  const navigate = useNavigate();
  const loc = useLocation() as any;
  const college: string =
    loc?.state?.college ||
    (sessionStorage.getItem("college") ?? "UNKNOWN_COLLEGE");

  const myRole: "faculty" | "admin" = "faculty";
  const uid = auth.currentUser?.uid || "FACULTY_ME";

  const [tab, setTab] = React.useState<"book" | "week" | "pods" | "util">(
    "book"
  );

  const [weekStartLocal, setWeekStartLocal] = React.useState<Date>(
    startOfIsoWeekLocal(new Date())
  );
  const weekDays = React.useMemo(
    () => getWeekDaysFrom(weekStartLocal),
    [weekStartLocal]
  );
  const timeGrid = React.useMemo(() => buildTimeGrid(9, 20), []);

  const [pods, setPods] = React.useState<Record<string, Pod>>({});
  const [bookings, setBookings] = React.useState<Booking[]>([]);
  const [pending, setPending] = React.useState<Pending | null>(null);

  // For legacy name lookups (kept intact). Now used as a fallback only.
  const [userNames, setUserNames] = React.useState<Record<string, string>>({});

  const weekStartMs = weekStartLocal.getTime();
  const weekEndMs = weekStartMs + 7 * 86400000;

  // Simple caps for admin self-booking (kept as-is)
  const myWeekBookings = bookings.filter(
    (b) => b.userId === uid && b.startMs >= weekStartMs && b.startMs < weekEndMs
  );
  const myMinutes = myWeekBookings.reduce(
    (m, b) => m + (b.endMs - b.startMs) / 60000,
    0
  );
  const mySessions = myWeekBookings.length;

  function canBook(durationMin: 15 | 30) {
    if (mySessions >= 2)
      return { ok: false, reason: "You reached 2 sessions this week (max 2)." };
    if (myMinutes + durationMin > 60)
      return {
        ok: false,
        reason: "Weekly limit is 60 minutes for faculty (max 2 sessions).",
      };
    return { ok: true };
  }

  /* ----- Pods (live) with one-time seeding if empty ----- */
  React.useEffect(() => {
    if (!college) return;
    const podsCol = collection(fs, "scheduler", college, "pods");
    let seeded = false;

    const unsub = onSnapshot(podsCol, async (snap) => {
      if (snap.empty && !seeded) {
        seeded = true;
        const batch = writeBatch(fs);
        const defaults: Record<string, Pod> = {
          "Pod-1": { name: "Pod-1", active: true },
          "Pod-2": { name: "Pod-2", active: true },
          "Pod-3": { name: "Pod-3", active: true },
          "Pod-4": { name: "Pod-4", active: true },
        };
        Object.entries(defaults).forEach(([id, pod]) => {
          batch.set(doc(fs, "scheduler", college, "pods", id), pod);
        });
        await batch.commit();
        return; // next snapshot will carry the seeded data
      }

      const map: Record<string, Pod> = {};
      snap.forEach(
        (d) => (map[d.id] = (d.data() as Pod) || { name: d.id, active: true })
      );
      setPods(map);
    });

    return () => unsub();
  }, [college]);

  /* ----- Bookings (live) for the selected week ----- */
  React.useEffect(() => {
    if (!college) return;
    const bookingsCol = collection(
      fs,
      "scheduler",
      college,
      "weeks",
      String(weekStartMs),
      "bookings"
    );
    const unsub = onSnapshot(bookingsCol, (snap) => {
      const list: Booking[] = [];
      snap.forEach((d) => {
        const v = d.data() as Booking;
        if (
          v &&
          typeof v.podId === "string" &&
          typeof v.startMs === "number" &&
          typeof v.endMs === "number" &&
          typeof v.userId === "string"
        ) {
          list.push(v);
        }
      });
      setBookings(list);
    });
    return () => unsub();
  }, [college, weekStartMs]);

  /* Keep legacy RTDB name resolution as a fallback for old bookings */
  React.useEffect(() => {
    const toLookup = Array.from(
      new Set(bookings.map((b) => b.userId).filter((id) => !userNames[id]))
    );
    if (toLookup.length === 0) return;

    const root = rref(db);

    (async () => {
      const updates: Record<string, string> = {};
      for (const id of toLookup) {
        let name = "";
        // Try a few plausible paths you used historically:
        const paths = [
          `Users/${id}/profile/name`,
          `users/${id}/name`,
          `users/${id}/profile/name`,
          `profiles/${id}/displayName`,
        ];
        for (const p of paths) {
          try {
            const snap = await rget(rref(db, p));
            const val = snap.val();
            if (typeof val === "string" && val.trim()) {
              name = val.trim();
              break;
            }
          } catch {
            /* ignore and try next path */
          }
        }
        // store if found
        if (name) updates[id] = name;
      }
      if (Object.keys(updates).length > 0) {
        setUserNames((prev) => ({ ...prev, ...updates }));
      }
    })();
  }, [bookings, userNames]);

  /* ----- Interactions ----- */

  function onCellClick(
    e: React.MouseEvent<HTMLButtonElement>,
    podId: string,
    dayLocal: Date,
    hh: number,
    mm: number
  ) {
    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
    const x = Math.min(rect.left + rect.width + 8, window.innerWidth - 220);
    const y = Math.min(rect.top, window.innerHeight - 120);
    setPending({ podId, dayLocal, hh, mm, x, y });
  }

  async function handleBook(duration: 15 | 30) {
    if (!pending) return;
    const check = canBook(duration);
    if (!check.ok) {
      alert(check.reason);
      return;
    }
    const start = new Date(
      pending.dayLocal.getFullYear(),
      pending.dayLocal.getMonth(),
      pending.dayLocal.getDate(),
      pending.hh,
      pending.mm,
      0,
      0
    );
    const startMs = start.getTime();
    const endMs = startMs + duration * 60_000;

    // Prevent overlaps (client-side)
    const overlap = bookings.some(
      (b) =>
        b.podId === pending.podId &&
        Math.max(b.startMs, startMs) < Math.min(b.endMs, endMs)
    );
    if (overlap) {
      alert("That slot is already taken.");
      return;
    }

    const bookingId = `${pending.podId}_${startMs}`;
    const bookingDoc = doc(
      fs,
      "scheduler",
      college,
      "weeks",
      String(weekStartMs),
      "bookings",
      bookingId
    );

    // NEW: resolve display name now and store with the booking
    const userName = getDisplayUserName();

    await setDoc(bookingDoc, {
      podId: pending.podId,
      startMs,
      endMs,
      userId: uid,

      userName, // NEW
      byRole: myRole,
    } as Booking);

    setPending(null);
    setTab("week");
  }

  async function cancelBooking(b: Booking) {
    const bookingId = `${b.podId}_${b.startMs}`;
    await deleteDoc(
      doc(
        fs,
        "scheduler",
        college,
        "weeks",
        String(weekStartMs),
        "bookings",
        bookingId
      )
    );
  }

  async function addPod() {
    const nid = findNextPodId(pods);
    await setDoc(doc(fs, "scheduler", college, "pods", nid), {
      name: nid,
      active: true,
    } as Pod);
  }
  async function togglePod(id: string) {
    await updateDoc(doc(fs, "scheduler", college, "pods", id), {
      active: pods[id]?.active === false ? true : false,
    } as Partial<Pod>);
  }
  async function renamePod(id: string, name: string) {
    await updateDoc(doc(fs, "scheduler", college, "pods", id), {
      name: name || id,
    } as Partial<Pod>);
  }
  async function deletePod(id: string) {
    await deleteDoc(doc(fs, "scheduler", college, "pods", id));
  }

  /* ----- Derived ----- */

  const podIds = React.useMemo(
    () =>
      Object.keys(pods)
        .filter((k) => pods[k]?.active !== false)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [pods]
  );

  return (
    <div className="container" style={{ position: "relative" }}>
      <div className="row" style={{ alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>Scheduler — Admin</h2>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>
      </div>

      <div className="row" style={{ marginTop: 12, flexWrap: "wrap", gap: 8 }}>
        <div className="pill">
          College: <b>{college}</b>
        </div>
        <div className="pill">
          Week:{" "}
          <b>
            {fmtDate(weekDays[0])} – {fmtDate(weekDays[6])}
          </b>
        </div>
        <div className="pill">
          Allowance:{" "}
          <b>
            {myMinutes}/60 min • {mySessions}/2 sessions
          </b>
        </div>
      </div>

      <div
        className="row"
        style={{ marginTop: 12, justifyContent: "space-between", gap: 12 }}
      >
        <div className="row" style={{ gap: 8 }}>
          <button
            className={`btn ${tab === "book" ? "primary" : ""}`}
            onClick={() => setTab("book")}
          >
            Book
          </button>
          <button
            className={`btn ${tab === "week" ? "primary" : ""}`}
            onClick={() => setTab("week")}
          >
            Week View
          </button>
          <button
            className={`btn ${tab === "pods" ? "primary" : ""}`}
            onClick={() => setTab("pods")}
          >
            Pod Management
          </button>
          <button
            className={`btn ${tab === "util" ? "primary" : ""}`}
            onClick={() => setTab("util")}
          >
            Utilization
          </button>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn"
            onClick={() => setWeekStartLocal(addDays(weekStartLocal, -7))}
          >
            ‹ Prev
          </button>
          <button
            className="btn"
            onClick={() => setWeekStartLocal(startOfIsoWeekLocal(new Date()))}
          >
            This week
          </button>
          <button
            className="btn"
            onClick={() => setWeekStartLocal(addDays(weekStartLocal, 7))}
          >
            Next ›
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: 12, overflowX: "auto" }}>
        {tab === "book" && (
          <WeekGrid
            pods={podIds}
            podsMap={pods}
            weekDays={weekDays}
            timeGrid={timeGrid}
            bookings={bookings}
            onCellClick={onCellClick}
            uid={uid}
          />
        )}
        {tab === "week" && (
          <WeekTable
            rows={[...bookings]
              .filter((b) => b.startMs >= weekStartMs && b.startMs < weekEndMs)
              .sort((a, b) => a.startMs - b.startMs)}
            pods={pods}
            userNames={userNames}
            onCancel={cancelBooking}
          />
        )}
        {tab === "pods" && (
          <PodManager
            podIds={podIds}
            pods={pods}
            onAdd={addPod}
            onToggle={togglePod}
            onRename={renamePod}
            onDelete={deletePod}
          />
        )}
        {tab === "util" && (
          <Utilization
            pods={pods}
            rows={[...bookings]
              .filter((b) => b.startMs >= weekStartMs && b.startMs < weekEndMs)
              .sort((a, b) => a.startMs - b.startMs)}
          />
        )}
      </div>

      {/* Inline popover near the clicked slot */}
      {pending && (
        <div
          id="slot-popover"
          style={{
            position: "fixed",
            left: pending.x,
            top: pending.y,
            zIndex: 9999,
            background: "var(--surface-1)",
            border: "1px solid var(--line)",
            borderRadius: 10,
            padding: 10,
            boxShadow: "0 10px 30px rgba(0,0,0,.35)",
            minWidth: 220,
          }}
        >
          <div className="small" style={{ marginBottom: 8 }}>
            <b>{pods[pending.podId]?.name || pending.podId}</b> —{" "}
            {fmtDate(pending.dayLocal)} {pad(pending.hh)}:{pad(pending.mm)}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn primary" onClick={() => handleBook(15)}>
              Book 15 min
            </button>
            <button className="btn" onClick={() => handleBook(30)}>
              Book 30 min
            </button>
            <button className="btn" onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- UI parts ---------------- */

function WeekGrid({
  pods,
  podsMap,
  weekDays,
  timeGrid,
  bookings,
  onCellClick,
  uid,
}: {
  pods: string[];
  podsMap: Record<string, Pod>;
  weekDays: Date[];
  timeGrid: { hh: number; mm: number }[];
  bookings: Booking[];
  onCellClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    podId: string,
    dayLocal: Date,
    hh: number,
    mm: number
  ) => void;
  uid: string;
}) {
  if (pods.length === 0) return <p className="small">No active pods.</p>;
  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `140px repeat(${pods.length}, 1fr)`,
          gap: 6,
        }}
      >
        <div />
        {pods.map((p) => (
          <div key={p} className="pill" style={{ textAlign: "center" }}>
            {podsMap[p]?.name || p}
          </div>
        ))}
      </div>

      {weekDays.map((day) => (
        <div key={String(day)} style={{ marginTop: 6 }}>
          <div
            className="pill muted"
            style={{ display: "inline-block", marginBottom: 6 }}
          >
            {day.toLocaleDateString(undefined, {
              weekday: "long",
              month: "short",
              day: "numeric",
            })}
          </div>
          {timeGrid.map(({ hh, mm }) => (
            <div
              key={`${hh}:${mm}`}
              style={{
                display: "grid",
                gridTemplateColumns: `140px repeat(${pods.length}, 1fr)`,
                gap: 6,
              }}
            >
              <div
                className="small"
                style={{ opacity: 0.7, textAlign: "right", paddingRight: 8 }}
              >
                {pad(hh)}:{pad(mm)}
              </div>
              {pods.map((podId) => {
                const startMs = new Date(
                  day.getFullYear(),
                  day.getMonth(),
                  day.getDate(),
                  hh,
                  mm,
                  0,
                  0
                ).getTime();
                const endMs = startMs + 15 * 60_000;

                const hit = bookings.find(
                  (b) =>
                    b.podId === podId &&
                    Math.max(b.startMs, startMs) < Math.min(b.endMs, endMs)
                );
                let bg = "rgba(34,197,94,.12)"; // free (green tint)
                let border = "rgba(34,197,94,.5)";
                let label = "Free";
                const disabled = !!hit;
                if (hit) {
                  const mine = hit.userId === uid;
                  bg = mine ? "rgba(59,130,246,.12)" : "rgba(239,68,68,.12)";
                  border = mine ? "rgba(59,130,246,.5)" : "rgba(239,68,68,.5)";
                  label = mine ? "Your slot" : "Booked";
                }
                return (
                  <button
                    key={podId}
                    className="btn"
                    disabled={disabled}
                    onClick={(e) => onCellClick(e, podId, day, hh, mm)}
                    style={{
                      justifySelf: "stretch",
                      textAlign: "center",
                      background: bg,
                      borderColor: border,
                      fontSize: 12,
                      padding: "8px 6px",
                    }}
                    title={disabled ? label : "Click to book"}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function WeekTable({
  rows,
  pods,
  userNames,
  onCancel,
}: {
  rows: Booking[];
  pods: Record<string, Pod>;
  userNames: Record<string, string>;
  onCancel: (b: Booking) => void;
}) {
  if (rows.length === 0)
    return <p className="small">No bookings this week yet.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Pod</th>
          <th>When</th>
          <th>Duration</th>
          <th>Booked By</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((b) => {
          const d = new Date(b.startMs);
          const dur = (b.endMs - b.startMs) / 60000;
          const label =
            d.toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            }) +
            " " +
            d.toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            });
          const who = b.userName || userNames[b.userId] || b.userId; // NEW: prefer stored name
          return (
            <tr key={`${b.podId}_${b.startMs}`}>
              <td>{pods[b.podId]?.name || b.podId}</td>
              <td>{label}</td>
              <td>{dur} min</td>
              <td className="small">
                {who} {b.byRole ? `(${b.byRole})` : ""}
              </td>
              <td>
                <button className="btn" onClick={() => onCancel(b)}>
                  Cancel
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PodManager({
  podIds,
  pods,
  onAdd,
  onToggle,
  onRename,
  onDelete,
}: {
  podIds: string[];
  pods: Record<string, Pod>;
  onAdd: () => void;
  onToggle: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <button className="btn" onClick={onAdd}>
        + Add Pod
      </button>
      <table className="table">
        <thead>
          <tr>
            <th>Pod</th>
            <th>Status</th>
            <th>Rename</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {podIds.map((id) => (
            <tr key={id}>
              <td>{pods[id]?.name || id}</td>
              <td>{pods[id]?.active === false ? "Inactive" : "Active"}</td>
              <td>
                <input
                  style={{ width: 160 }}
                  defaultValue={pods[id]?.name || id}
                  onBlur={(e) => onRename(id, e.target.value)}
                />
              </td>
              <td>
                <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                  <button className="btn" onClick={() => onToggle(id)}>
                    {pods[id]?.active === false ? "Activate" : "Deactivate"}
                  </button>
                  <button className="btn" onClick={() => onDelete(id)}>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Utilization({
  pods,
  rows,
}: {
  pods: Record<string, Pod>;
  rows: Booking[];
}) {
  if (rows.length === 0)
    return <p className="small">No bookings this week yet.</p>;
  const perPod: Record<string, number> = {};
  rows.forEach((b) => {
    perPod[b.podId] = (perPod[b.podId] || 0) + (b.endMs - b.startMs) / 60000;
  });
  const list = Object.entries(perPod).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Pod</th>
          <th>Booked Minutes</th>
        </tr>
      </thead>
      <tbody>
        {list.map(([id, mins]) => (
          <tr key={id}>
            <td>{pods[id]?.name || id}</td>
            <td>{mins}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* --------------- Helpers --------------- */

function findNextPodId(pods: Record<string, Pod>) {
  const ids = Object.keys(pods);
  let n = 1;
  while (ids.includes(`Pod-${n}`)) n++;
  return `Pod-${n}`;
}
